import React, { useEffect, useState } from "react";
import { Message } from "../../chat";
import MarkdownIt from "markdown-it";
import markdownItHighlight from "markdown-it-highlightjs";
import FileIcon from "../../assest/svg/file.svg";
import { useLinkClickMutaion } from "../../api/mutation/useLinkClickMutation";
import Logo from "../../assest/favicon.png";

export const ChatBox = ({
  messages,
  chatBox,
}: {
  messages: Message[];
  chatBox: React.RefObject<HTMLDivElement>;
}) => {
  const cleanInputText = (inputText: string): string => {
    return inputText.replace(/【[^】]+】/g, ""); // Remove 【...】 blocks
  };

  const decodeText = (inputText: string): string => {
    try {
      const md = MarkdownIt({
        html: true,
        linkify: true,
        typographer: true,
        breaks: true,
      }).use(markdownItHighlight);

      // Custom rule to open links in a new tab
      const defaultRender =
        md.renderer.rules.link_open ||
        ((tokens, idx, options, env, self) =>
          self.renderToken(tokens, idx, options));

      md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
        const hrefIndex = tokens[idx].attrIndex("target");
        if (hrefIndex < 0) {
          tokens[idx].attrPush(["target", "_blank"]);
          tokens[idx].attrPush(["data-clickable", "true"]);
          tokens[idx].attrPush([
            "class",
            "text-cyan-600 dark:text-cyan-400 hover:underline font-medium transition-colors",
          ]);
        } else {
          tokens[idx].attrs![hrefIndex][1] = "_blank";
        }
        return defaultRender(tokens, idx, options, env, self);
      };

      // Clean the input before processing
      const cleanedInput = cleanInputText(inputText);

      // Process and render Markdown
      const parts = cleanedInput.split(/(```[\s\S]*?```)/g);

      const processedParts = parts.map((part) =>
        part.startsWith("```") ? part : part.replace(/\n\n/g, "\n&#8203;\n")
      );

      const processedInput = processedParts.join("");
      return md.render(processedInput, "markdown");
    } catch (error) {
      console.error("Markdown render error:", error);
      return `${error}`;
    }
  };

  const linkClickMutation = useLinkClickMutaion();
  const handleLinkClick = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as Element;
    if (target.tagName === "A" && target.getAttribute("data-clickable") === "true") {
      const url = target.getAttribute("href") || target.attributes[0]?.value;
      if (url) {
        linkClickMutation.mutate(
          { url },
          {
            onSettled() {
              console.info("Click recorded on:", url);
            },
          }
        );
      }
    }
  };

  const [isAtBottom, setIsAtBottom] = useState(true);

  // Handle user scrolling
  const handleScroll = () => {
    if (chatBox.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatBox.current;
      const nearBottom = scrollTop + clientHeight >= scrollHeight - 40;
      setIsAtBottom(nearBottom);
    }
  };

  // Attach scroll listener
  useEffect(() => {
    const chatContainer = chatBox.current;
    if (chatContainer) {
      chatContainer.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (chatContainer) {
        chatContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // Auto-scroll only if user was near bottom
  useEffect(() => {
    if (chatBox.current && isAtBottom) {
      chatBox.current.scroll({
        top: chatBox.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, isAtBottom]);

  const scrollToBottom = () => {
    if (chatBox.current) {
      chatBox.current.scroll({
        top: chatBox.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return "";
    try {
      const d = new Date(timestamp);
      return !isNaN(d.getTime())
        ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        : "";
    } catch {
      return "";
    }
  };

  return (
    <div className="flex-1 py-3 px-1 space-y-6 relative" onClick={handleLinkClick}>
      {messages.map((message, index) => {
        const isUser = message.role === "user";
        const timeStr = formatTimestamp(message.timestamp);

        return isUser ? (
          /* USER MESSAGE ROW */
          <div key={index} className="flex flex-col items-end gap-1.5 animate-fadeIn">
            {/* Metadata Label */}
            <div className="flex items-center gap-2 text-[11px] text-slate-400 dark:text-slate-500 mr-1">
              <span className="font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                Vous
              </span>
              {timeStr && <span>{timeStr}</span>}
              <div className="w-5 h-5 rounded-full bg-gradient-to-tr from-purple-600 to-indigo-500 text-white flex items-center justify-center text-[10px] font-bold">
                U
              </div>
            </div>

            {/* Bubble */}
            <div className="max-w-[85%] sm:max-w-[75%] rounded-2xl p-4 bg-[#1a1e2d] dark:bg-[#1a1e2d] bg-purple-50 text-slate-100 dark:text-slate-100 text-slate-800 border border-purple-500/20 dark:border-white/10 shadow-md">
              {/* Attached Files */}
              {message.image_urls && message.image_urls.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {message.image_urls.map((file, fileIdx) => {
                    const fileExtension = file.split(".").pop()?.toLowerCase();
                    const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(
                      fileExtension ?? ""
                    );

                    return isImage ? (
                      <div
                        key={fileIdx}
                        className="w-32 h-32 rounded-xl overflow-hidden border border-white/10 bg-black/20"
                      >
                        <img
                          className="w-full h-full object-cover"
                          src={file}
                          alt="Fichier joint"
                        />
                      </div>
                    ) : (
                      <div
                        key={fileIdx}
                        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-black/30 border border-white/10 text-xs text-slate-200 shadow-sm max-w-xs"
                      >
                        <img
                          src={FileIcon}
                          className="w-5 h-5 dark:invert opacity-80"
                          alt="Fichier"
                        />
                        <a
                          href={file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-400 hover:underline truncate"
                        >
                          {file.split("/").pop()?.split("_").slice(-2).join("_") || "Fichier joint"}
                        </a>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Message text */}
              <div className="text-sm leading-relaxed whitespace-pre-wrap font-sans">
                {message.content}
              </div>
            </div>
          </div>
        ) : (
          /* ASSISTANT MESSAGE ROW */
          <div key={index} className="flex flex-col items-start gap-1.5 animate-fadeIn">
            {/* Metadata Label */}
            <div className="flex items-center gap-2 text-[11px] text-slate-400 dark:text-slate-500 ml-1">
              <div className="w-5 h-5">
                <img src={Logo} className="w-5 h-5 object-contain" alt="Continuum" />
              </div>
              <span className="font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-200">
                Continuum Intelligence
              </span>
              {timeStr && <span>{timeStr}</span>}
            </div>

            {/* Bubble */}
            <div className="w-full rounded-2xl p-5 bg-white/90 dark:bg-[#12151f]/90 border border-slate-200/90 dark:border-white/10 shadow-lg text-slate-800 dark:text-slate-100">
              <div
                className="prose dark:prose-invert max-w-none text-sm leading-relaxed font-sans"
                dangerouslySetInnerHTML={{
                  __html: decodeText(message.content),
                }}
              />
            </div>
          </div>
        );
      })}

      {/* Floating Scroll to Bottom Button */}
      {!isAtBottom && (
        <button
          type="button"
          onClick={scrollToBottom}
          className="fixed bottom-24 right-8 z-40 p-2.5 rounded-full bg-[#161924] text-white border border-white/10 shadow-2xl hover:bg-purple-600 transition-all duration-200 hover:scale-105"
          title="Faire défiler vers le bas"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ChatBox;
